import { LocalizedLink } from '@/_ui'
import { cn, ts } from '@/lib'
import type { Tablature } from '@/types/tablatures'
import { Card } from '@ui/composites/Card'
import { Badge } from '@ui/primitives/Badge'
import { Heading } from '@ui/primitives/Heading'
import { Text } from '@ui/primitives/Text'
import Link from 'next/link'
import slug from 'slug'
import { KeyBadge } from './TablatureBadges'

interface TablatureCardProps {
  tab: Tablature
  index?: number
  dictionary: any
  className?: string
  variant?: 'compact' | 'full'
  locale: string
}

export function TablatureCard({
  tab,
  index,
  dictionary,
  className = '',
  variant = 'compact',
  locale
}: TablatureCardProps) {
  const isFull = variant === 'full'
  const artists = tab.artists?.map(a => a.name).join(', ')
  return (
    <Card
      className={`group hover:shadow-md transition-all duration-200 border-none shadow-none hover:shadow-none hover:bg-transparent ${className}`}
      hover
    >
      <Card.Body className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-4 flex-1">
          {index && (
            <div className="flex-shrink-0 w-8 h-8 bg-secondary-50 text-secondary-800 rounded-full flex items-center justify-center text-sm font-semibold">
              {index}
            </div>
          )}

          <div className="flex-1 min-w-0">
            <Heading
              as="h3"
              size="xs"
              className="mb-1 truncate group-hover:text-[var(--color-primary-700)] transition-colors"
            >
              <LocalizedLink
                href={`/tablatures/${tab.id}/${slug(tab.title, { lower: true })}`}
                locale={locale}
              >
                {tab.title}
              </LocalizedLink>
            </Heading>
            <Text
              size="sm"

              className="truncate"
              title={`Auteur: ${artists}`}
            >
              {artists ? artists : <span>&nbsp;</span>}
            </Text>

          </div>
        </div>

        {isFull ? (
          <>
            <div className="flex items-start gap-4 flex-1">
              <DifficultyBadge difficulty={tab.difficulty} dictionary={dictionary} />
            </div>
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 flex-shrink-0">
              <TypeBadge type={tab.type} dictionary={dictionary} />
              {tab.key && <KeyBadge value={tab.key} dictionary={dictionary} />}
              <CategoryBadge category={tab.category} dictionary={dictionary} />
              {tab.status !== 'public' && (
                <StatusBadge status={tab.status} dictionary={dictionary} />
              )}
            </div>
          </>
        ) : (
          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 flex-shrink-0">
            <DifficultyBadge difficulty={tab.difficulty} dictionary={dictionary} />
            {tab.status !== 'public' && (
              <StatusBadge status={tab.status} dictionary={dictionary} />
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

function DifficultyBadge({ difficulty, dictionary }: { difficulty: string; dictionary: any }) {
  const variants = cn({
    'bg-green-600 text-white rounded-sm px-2 py-1 text-xs text-center inline-block w-22': difficulty === 'easy',
    'bg-yellow-600 text-white rounded-sm px-2 py-1 text-xs text-center inline-block w-22': difficulty === 'medium',
    'bg-red-600 text-white rounded-sm px-2 py-1 text-xs text-center inline-block w-22': difficulty === 'hard',
    'bg-purple-600 text-white rounded-sm px-2 py-1 text-xs text-center inline-block w-22': difficulty === 'expert',
  })

  return (
    <Badge variant={'default'} className={variants} size="sm">
      {dictionary?.difficulty?.[difficulty] || difficulty}
    </Badge>
  )
}

function TypeBadge({ type, dictionary }: { type: string; dictionary: any }) {
  return (
    <Badge size="sm">
      {dictionary?.tablatures?.types?.[type] || type}
    </Badge>
  )
}

function CategoryBadge({ category, dictionary }: { category: string; dictionary: any }) {
  return (
    <Badge size="sm">
      {dictionary?.tablatures?.categories?.[category] || category}
    </Badge>
  )
}

function StatusBadge({ status, dictionary }: { status: string; dictionary: any }) {
  return (
    <Badge size="sm">
      {dictionary?.tablatures?.status?.[status] || status}
    </Badge>
  )
}
